/* snt-l26 — landing page behaviour.

   Two jobs:
   1. Hide the theme header / announcement bar. This is a standalone landing
      page for paid traffic; the previous 2026 page did the same thing with a
      custom-liquid <style> block that hard-coded section ids. Ids change when
      a section group is re-saved, so this matches on the stable
      `shopify-section-group-header-group` class the theme emits, with the id
      form kept as a fallback.
   2. A short fade for content below the fold. Anything already on screen when
      the page loads is shown immediately — a paid-traffic page must never wait
      on JavaScript to paint its first screen.
*/
(function () {
  'use strict';

  if (window.__sntL26Bound) return;
  window.__sntL26Bound = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function reveal() {
    var targets = document.querySelectorAll('.snt-l26 [data-snt-reveal]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var deferred = [];
    targets.forEach(function (el) {
      if (el.getBoundingClientRect().top < (window.innerHeight || 0)) {
        el.classList.add('is-visible');
      } else {
        el.classList.add('snt-l26__reveal');
        deferred.push(el);
      }
    });
    if (!deferred.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });

    deferred.forEach(function (el) { io.observe(el); });
  }

  // Promote a deferred poster. Below-the-fold videos ship their still in
  // `data-snt-poster` rather than `poster`, because a poster attribute is
  // fetched eagerly no matter where the video sits on the page — there is no
  // `loading="lazy"` for video stills. Moving it to a data attribute and
  // promoting it on approach kept 121 KB of off-screen JPEG out of the initial
  // load. The CSS gives every one of these boxes an aspect-ratio, so the layout
  // is already the right height before the still or the metadata arrives.
  function poster(v) {
    var src = v.getAttribute('data-snt-poster');
    if (src && !v.getAttribute('poster')) v.setAttribute('poster', src);
  }

  // Autoplay can be refused (Low Power Mode, data saver). If it is, the poster
  // simply stays up. We deliberately do NOT fall back to native controls: these
  // videos are decorative set-dressing, and a play/fullscreen chrome appearing
  // on hover breaks the illusion. Poster frames are chosen to stand alone.
  //
  // THE THING THAT MATTERS HERE: play() is only ever called on a video that is
  // on screen. Calling it unconditionally at DOMContentLoaded — which this did
  // — overrides `preload="none"` and pulls the entire file down, so two
  // ambient loops the visitor had not scrolled to yet (2.27 MB between them)
  // were competing with the hero for a throttled connection. Gating on
  // intersection is worth far more than any amount of re-encoding.
  //
  // Leaving the viewport pauses again: three simultaneous video decodes running
  // unseen for the whole visit is a battery and main-thread cost with no
  // matching benefit.
  function video() {
    var vids = [].slice.call(document.querySelectorAll('.snt-l26 video[data-snt-autoplay]'));
    if (!vids.length) return;

    function start(v) {
      poster(v);
      var play = v.play();
      if (play && typeof play.catch === 'function') {
        play.catch(function () { /* poster remains */ });
      }
    }

    if (!('IntersectionObserver' in window)) { vids.forEach(start); return; }

    // No margin at all: a clip is fetched when it is genuinely on screen and
    // not one pixel earlier. On a 412x823 phone the statement section's backdrop
    // starts 29px below the fold, so even a 50px margin pulled its 1.4 MB during
    // the initial load. warmPosters() runs 400px ahead, so the still is already
    // in place and what arrives late is motion, not the picture.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) start(entry.target);
        else if (!entry.target.paused) entry.target.pause();
      });
    }, { rootMargin: '0px' });

    vids.forEach(function (v) { io.observe(v); });
  }

  // Videos marked data-snt-play-in-view stay paused until they are on screen,
  // then start from frame 1 — an assembly reveal is worthless joined halfway.
  // Threshold stays higher than the ambient loops above: a reveal should begin
  // when the block is properly in front of the reader, not at its first pixel.
  function playInView() {
    var vids = document.querySelectorAll('.snt-l26 video[data-snt-play-in-view]');
    if (!vids.length) return;
    if (!('IntersectionObserver' in window)) {
      vids.forEach(function (v) { poster(v); v.play().catch(function () {}); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          poster(v);
          if (v.paused) { try { v.currentTime = 0; } catch (e) {} v.play().catch(function () {}); }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.4 });
    vids.forEach(function (v) { io.observe(v); });
  }

  // One pass over every deferred poster, on a wider margin than either playback
  // gate, so the still is decoded and in place before the block is reached.
  function warmPosters() {
    var vids = [].slice.call(document.querySelectorAll('.snt-l26 video[data-snt-poster]'));
    if (!vids.length) return;
    if (!('IntersectionObserver' in window)) { vids.forEach(poster); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        poster(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '400px 0px' });
    vids.forEach(function (v) { io.observe(v); });
  }

  // Scroll-linked emphasis. Each group lights exactly one child, chosen from
  // how far the group's centre has travelled up the viewport — so it behaves
  // identically on a phone and a desktop, because it is measured in viewport
  // fractions rather than pixels. Reads are batched into a rAF so scrolling
  // never triggers layout thrash.
  function litGroups() {
    var groups = [].slice.call(document.querySelectorAll('.snt-l26 [data-snt-lit-group]'));
    if (!groups.length) return;

    groups.forEach(function (g) { g.classList.add('is-ready'); });

    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      groups.forEach(function (g) {
        var items = g.querySelectorAll('[data-snt-lit]');
        if (!items.length) return;
        var box = g.getBoundingClientRect();
        var centre = box.top + box.height / 2;
        // 0 when the group sits at the bottom of the screen, 1 at the top.
        var t = 1 - (centre / vh);
        var i = Math.floor(t * items.length);
        if (i < 0) i = 0;
        if (i > items.length - 1) i = items.length - 1;
        for (var n = 0; n < items.length; n++) {
          items[n].classList.toggle('is-lit', n === i);
        }
      });
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // Reveal and the lit groups are layout — they run as soon as the DOM is ready
  // or the first screen would sit wrong. Decorative media is the opposite: none
  // of it belongs in the critical path, so its observers are not even attached
  // until the load event has fired and the browser is otherwise idle. The hero
  // is unaffected — it keeps a real `autoplay` attribute and starts natively,
  // without waiting for this file at all.
  function init() { reveal(); litGroups(); }

  function initMedia() { warmPosters(); video(); playInView(); }

  function whenIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 200);
  }

  function afterLoad(fn) {
    if (document.readyState === 'complete') whenIdle(fn);
    else window.addEventListener('load', function () { whenIdle(fn); }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  afterLoad(initMedia);

  document.addEventListener('shopify:section:load', function () { init(); initMedia(); });
})();
